<template>
  <div class="area-isencao p-4">
    <h5 class="bold mb-4">
      <i class="fas fa-percentage me-2"></i>
      Solicitação de Isenção
    </h5>
    
    <!-- Loading Inscrições -->
    <div v-if="loadingInscricoes" class="loading-container">
      <div class="spinner-wrapper">
        <div class="spinner-border text-primary" role="status">
          <span class="visually-hidden">Carregando...</span>
        </div>
        <p class="loading-text mt-3">Carregando inscrições disponíveis...</p>
      </div>
    </div>
    
    <div v-else>
      <!-- Inscrições disponíveis para isenção -->
      <div class="section-header mb-3">
        <i class="fas fa-clipboard-list"></i>
        <span>Inscrições Disponíveis para Isenção</span>
      </div>

      <div v-if="!inscricoesAtivas.length" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-clipboard-list"></i>
        </div>
        <h6 class="empty-title">Nenhuma inscrição disponível</h6>
        <p class="empty-text">Você não possui inscrições ativas elegíveis para isenção.</p>
        <small class="empty-hint">Somente inscrições ativas podem solicitar isenção.</small>
      </div>

      <template v-else>
        <!-- Desktop Table View -->
        <div class="table-responsive d-none d-md-block">
          <table class="table table-hover">
            <thead class="table-dark">
              <tr>
                <th><i class="fas fa-building"></i> Concurso</th>
                <th><i class="fas fa-briefcase"></i> Vaga</th>
                <th><i class="fas fa-calendar-times"></i> Encerramento</th>
                <th><i class="fas fa-cogs"></i> Ações</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="inscricao in inscricoesAtivas" :key="inscricao.id">
                <td>
                  <strong>{{ inscricao.concurso?.titulo || 'N/A' }}</strong>
                  <br>
                  <small class="text-muted">{{ inscricao.concurso?.prefeitura || 'N/A' }}</small>
                </td>
                <td>{{ inscricao.vaga?.cargo || 'N/A' }}</td>
                <td>
                  <span :class="getClassePrazo(inscricao.concurso)">
                    {{ formatarDataHora(inscricao.concurso?.prazo_final_isencao) }}
                  </span>
                </td>
                <td>
                  <button 
                    class="btn btn-sm btn-primary" 
                    @click="abrirModal(inscricao)"
                    :disabled="!estaNoPrazo(inscricao.concurso)">
                    <i class="fas fa-paper-plane"></i>
                    {{ estaNoPrazo(inscricao.concurso) ? 'Solicitar' : 'Prazo Encerrado' }}
                  </button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobile Card View -->
        <div class="mobile-cards d-md-none">
        <div 
          v-for="inscricao in inscricoesAtivas" 
          :key="inscricao.id" 
          class="mobile-card"
        >
          <div class="card-header-mobile">
            <i class="fas fa-building"></i>
            <strong>{{ inscricao.concurso?.titulo || 'N/A' }}</strong>
          </div>
          <div class="card-body-mobile">
            <div class="card-item">
              <i class="fas fa-map-marker-alt"></i>
              <span class="label">Prefeitura:</span>
              <span class="value">{{ inscricao.concurso?.prefeitura || 'N/A' }}</span>
            </div>
            <div class="card-item">
              <i class="fas fa-briefcase"></i>
              <span class="label">Vaga:</span>
              <span class="value">{{ inscricao.vaga?.cargo || 'N/A' }}</span>
            </div>
            <div class="card-item">
              <i class="fas fa-calendar-times"></i>
              <span class="label">Encerramento:</span>
              <span :class="getClassePrazo(inscricao.concurso)" class="value">
                {{ formatarDataHora(inscricao.concurso?.prazo_final_isencao) }}
              </span>
            </div>
          </div>
          <div class="card-footer-mobile">
            <button 
              class="btn btn-primary btn-block-mobile" 
              @click="abrirModal(inscricao)"
              :disabled="!estaNoPrazo(inscricao.concurso)">
              <i class="fas fa-paper-plane"></i>
              {{ estaNoPrazo(inscricao.concurso) ? 'Solicitar Isenção' : 'Prazo Encerrado' }}
            </button>
          </div>
        </div>
      </div>
      </template>
      
      <!-- Minhas Isenções -->
      <div class="section-header mb-3 mt-5">
        <i class="fas fa-list-alt"></i>
        <span>Meus Pedidos Isenções</span>
      </div>

      <!-- Loading Isenções -->
      <div v-if="loadingIsencoes" class="loading-container">
        <div class="spinner-wrapper">
          <div class="spinner-border text-success" role="status">
            <span class="visually-hidden">Carregando...</span>
          </div>
          <p class="loading-text mt-3">Carregando suas isenções...</p>
        </div>
      </div>

      <div v-else-if="!isencoes.length" class="empty-state">
        <div class="empty-icon">
          <i class="fas fa-percentage"></i>
        </div>
        <h6 class="empty-title">Nenhuma isenção solicitada</h6>
        <p class="empty-text">Você ainda não possui solicitações de isenção.</p>
        <small class="empty-hint">Solicite isenção para suas inscrições ativas acima!</small>
      </div>

       <div v-else class="table-responsive">
         <table class="table table-hover">
           <thead class="table-dark">
             <tr>
               <th><i class="fas fa-calendar"></i> Data</th>
               <th><i class="fas fa-briefcase"></i> Vaga</th>
               <th><i class="fas fa-comment"></i> Motivo</th>
               <th><i class="fas fa-paperclip"></i> Anexo</th>
               <th><i class="fas fa-reply"></i> Resposta</th>
               <th><i class="fas fa-info-circle"></i> Situação</th>
               <th><i class="fas fa-cogs"></i> Ações</th>
             </tr>
           </thead>
           <tbody>
             <tr v-for="isencao in isencoes" :key="isencao.id">
               <td>{{ formatarData(isencao.created_at) }}</td>
               <td>{{ isencao.inscricao?.vaga?.cargo || 'N/A' }}</td>
               <td>
                 <span 
                   @click="abrirDetalhes(isencao, 'motivo')" 
                   class="pointer text-primary link-hover"
                   :title="isencao.motivo"
                 >
                   <i class="fas fa-eye"></i>
                   {{ limitarTexto(isencao.motivo, 30) }}
                 </span>
               </td>
               <td class="text-center">
                 <a 
                   v-if="isencao.caminho_anexo"
                   :href="isencao.caminho_anexo"
                   target="_blank"
                   class="btn btn-sm btn-info"
                   title="Visualizar anexo"
                 >
                   <i class="fas fa-file-download"></i>
                   Ver Anexo
                 </a>
                 <span v-else class="text-muted">
                   <i class="fas fa-minus"></i>
                   Sem anexo
                 </span>
               </td>
               <td>
                 <span 
                   @click="abrirDetalhes(isencao, 'resposta')" 
                   class="pointer text-primary link-hover"
                   :title="isencao.observacao_admin || 'Aguardando resposta'"
                 >
                   <i class="fas fa-eye"></i>
                   {{ limitarTexto(isencao.observacao_admin || 'Aguardando resposta', 30) }}
                 </span>
               </td>
               <td>
                 <span :class="getClasseStatus(isencao.status)" class="badge-status">
                   {{ legendaStatus(isencao.status) }}
                 </span>
               </td>
               <td>
                 <button 
                   class="btn btn-sm btn-danger" 
                   v-if="isencao.status === 'P'"
                   @click="cancelarIsencao(isencao.id)">
                   <i class="fas fa-times"></i>
                   Cancelar
                 </button>
                 <span v-else class="text-muted">-</span>
               </td>
             </tr>
           </tbody>
         </table>
       </div>
    </div>

    <!-- Modal de Solicitação de Isenção -->
    <div v-if="showModal" class="modal-overlay" @click="fecharModal">
      <div class="modal-content large" @click.stop>
        <!-- Loading Overlay no Modal -->
        <div v-if="loadingModal" class="modal-loading-overlay">
          <div class="spinner-wrapper">
            <div class="spinner-border text-light" role="status">
              <span class="visually-hidden">Carregando...</span>
            </div>
            <p class="loading-text text-light mt-3">Carregando informações...</p>
          </div>
        </div>

        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-percentage"></i>
            Solicitação de Isenção
          </h4>
          <button @click="fecharModal" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div v-if="inscricaoSelecionada" class="mb-3 info-box">
            <h6 class="text-primary">
              <i class="fas fa-building"></i>
              {{ inscricaoSelecionada.concurso?.prefeitura }}
            </h6>
            <p class="text-muted mb-1">{{ inscricaoSelecionada.concurso?.titulo }}</p>
            <p class="text-muted mb-0">
              <strong>Vaga:</strong> {{ inscricaoSelecionada.vaga?.cargo }}
            </p>
          </div>

          <div class="form-group">
            <label for="motivo" class="form-label">
              <i class="fas fa-comment"></i>
              Motivo da Solicitação <span class="text-danger">*</span>
            </label>
            <textarea 
              v-model="motivo" 
              class="form-control" 
              rows="4"
              placeholder="Descreva o motivo da sua solicitação de isenção..."
              :class="{ 'is-invalid': !motivo && tentouEnviar }"
            ></textarea>
            <div v-if="!motivo && tentouEnviar" class="invalid-feedback">
              O motivo é obrigatório
            </div>
          </div>

          <div class="form-group">
            <label class="form-label">
              <i class="fas fa-paperclip"></i>
              Anexar Comprovante
            </label>
            <input 
              type="file" 
              class="form-control" 
              @change="handleFileUpload"
              accept=".pdf,.jpg,.jpeg,.png"
            />
            <small class="form-text text-muted">
              Formatos aceitos: PDF, JPG, JPEG, PNG (máx. 5MB)
            </small>
            <div v-if="arquivo" class="file-info mt-2">
              <i class="fas fa-file"></i>
              <span>{{ arquivo.name }}</span>
              <span class="file-size">({{ formatFileSize(arquivo.size) }})</span>
              <button @click="removerArquivo" class="btn-remove-file">
                <i class="fas fa-times"></i>
              </button>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary mr-2" @click="fecharModal" :disabled="enviando">
            <i class="fas fa-times"></i>
            Cancelar
          </button>
          <button class="btn btn-primary" @click="enviarIsencao" :disabled="enviando">
            <span v-if="enviando">
              <span class="spinner-border spinner-border-sm me-2" role="status">
                <span class="visually-hidden">Enviando...</span>
              </span>
              Enviando...
            </span>
            <span v-else>
              <i class="fas fa-paper-plane"></i>
              Enviar Solicitação
            </span>
          </button>
        </div>
      </div>
    </div>

    <!-- Modal de Detalhes -->
    <div v-if="showDetalhes" class="modal-overlay" @click="fecharDetalhes">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h4 class="modal-title">
            <i class="fas fa-info-circle"></i>
            Detalhes
          </h4>
          <button @click="fecharDetalhes" class="modal-close">
            <i class="fas fa-times"></i>
          </button>
        </div>
        <div class="modal-body">
          <div class="detalhe-texto">
            {{ detalheTexto }}
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn btn-secondary" @click="fecharDetalhes">
            <i class="fas fa-times"></i>
            Fechar
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'SolicitarIsencao',
  data() {
    return {
      loadingInscricoes: false,
      loadingIsencoes: false,
      loadingModal: false,
      enviando: false,
      tentouEnviar: false,
      inscricoes: [],
      isencoes: [],
      showModal: false,
      showDetalhes: false,
      detalheTexto: '',
      inscricaoSelecionada: null,
      motivo: '',
      arquivo: null
    }
  },
  computed: {
    // Filtrar apenas inscrições ativas (status 'A')
    inscricoesAtivas() {
      return this.inscricoes.filter(inscricao => inscricao.status === 'A')
    }
  },
  created() {
    this.carregarInscricoes()
    this.carregarIsencoes()
  },
  methods: {
    async carregarInscricoes() {
      try {
        this.loadingInscricoes = true
        const { data } = await axios.get('/ps/inscricao')
        console.log('Inscrições carregadas:', data)
        this.inscricoes = data || []
        console.log('Inscrições ativas filtradas:', this.inscricoesAtivas.length)
      } catch (error) {
        console.error('Erro ao carregar inscrições:', error)
        this.inscricoes = []
        this.$sweet.sweetMensagem("Erro ao carregar inscrições. Tente novamente.")
      } finally {
        this.loadingInscricoes = false
      }
    },
    
    async carregarIsencoes() {
      try {
        this.loadingIsencoes = true
        const { data } = await axios.get('/ps/isencao/inscricao')
        console.log('Isenções carregadas:', data)
        this.isencoes = data || []
      } catch (error) {
        console.error('Erro ao carregar isenções:', error)
        this.isencoes = []
      } finally {
        this.loadingIsencoes = false
      }
    },
    
    abrirModal(inscricao) {
      console.log('Abrindo modal para inscrição:', inscricao)
      this.showModal = true
      this.loadingModal = true
      
      // Simular um pequeno delay para mostrar o loading (pode ser removido se houver uma requisição real)
      setTimeout(() => {
        this.inscricaoSelecionada = inscricao
        this.motivo = ''
        this.arquivo = null
        this.tentouEnviar = false
        this.loadingModal = false
      }, 300)
    },

    fecharModal() {
      this.showModal = false
      this.loadingModal = false
      this.inscricaoSelecionada = null
      this.motivo = ''
      this.arquivo = null
      this.tentouEnviar = false
      this.enviando = false
    },

    fecharDetalhes() {
      this.showDetalhes = false
      this.detalheTexto = ''
    },
    async cancelarIsencao(id) {
      try {
        const confirm = await this.$sweet.sweetQuestionPersonalizado(
          "Cancelar Solicitação", 
          "Tem certeza que deseja cancelar sua solicitação de isenção?"
        )
        
        if (confirm.isDismissed === true) return
        
        await axios.delete(`/ps/isencao/${id}`)
        await this.carregarIsencoes()
        
        this.$sweet.sweetSuccess("Solicitação cancelada com sucesso!")
      } catch (error) {
        console.error('Erro ao cancelar solicitação:', error)
        this.$sweet.sweetError("Erro ao cancelar solicitação.")
      }
    },
    
    estaNoPrazo(concurso) {
      if (!concurso || !concurso.prazo_final_isencao) return false
      
      const hoje = new Date()
      const fim = new Date(concurso.prazo_final_isencao)
      return hoje <= fim
    },
    
    getClassePrazo(concurso) {
      if (!concurso || !concurso.prazo_final_isencao) return 'text-muted'
      
      const hoje = new Date()
      const fim = new Date(concurso.prazo_final_isencao)
      const diasRestantes = Math.ceil((fim - hoje) / (1000 * 60 * 60 * 24))
      
      if (diasRestantes < 0) return 'text-danger font-weight-bold'
      if (diasRestantes <= 3) return 'text-warning font-weight-bold'
      return 'text-success'
    },
    
    getClasseStatus(status) {
      const classes = {
        'P': 'badge-warning',
        'D': 'badge-success',
        'I': 'badge-danger'
      }
      return classes[status] || 'badge-secondary'
    },
    
    limitarTexto(texto, limite) {
      if (!texto) return '-'
      return texto.length > limite ? texto.substring(0, limite) + '...' : texto
    },
    
    abrirDetalhes(isencao, tipo) {
      this.detalheTexto = tipo === 'motivo' 
        ? (isencao.motivo || 'Sem motivo informado') 
        : (isencao.observacao_admin || 'Aguardando resposta do administrador')
      this.showDetalhes = true
    },
    
    async enviarIsencao() {
      this.tentouEnviar = true
      
      // Validações
      if (!this.motivo || !this.motivo.trim()) {
        this.$sweet.sweetError("Informe o motivo da solicitação.")
        return
      }
      
      if (this.arquivo && this.arquivo.size > 5 * 1024 * 1024) {
        this.$sweet.sweetError("O arquivo deve ter no máximo 5MB.")
        return
      }
      
      const formData = new FormData()
      formData.append("motivo", this.motivo.trim())
      formData.append("inscricao_id", this.inscricaoSelecionada.id)
      
      if (this.arquivo) {
        formData.append("arquivo", this.arquivo)
      }
      
      try {
        this.enviando = true
        console.log('Enviando solicitação de isenção...', {
          inscricao_id: this.inscricaoSelecionada.id,
          motivo: this.motivo.substring(0, 50) + '...',
          tem_arquivo: !!this.arquivo
        })
        
        await axios.post(
          `/ps/isencao/${this.inscricaoSelecionada.id}/solicitar`, 
          formData, 
          {
            headers: { "Content-Type": "multipart/form-data" }
          }
        )
        
        this.fecharModal()
        await this.carregarIsencoes()
        
        this.$sweet.sweetSuccess("Solicitação enviada com sucesso!")
      } catch (error) {
        console.error('Erro ao enviar solicitação:', error)
        
        let errorMessage = "Erro ao enviar solicitação."
        
        if (error.response?.data?.error) {
          errorMessage = error.response.data.error
        } else if (error.response?.data?.message) {
          errorMessage = error.response.data.message
        }
        
        this.$sweet.sweetError(errorMessage)
      } finally {
        this.enviando = false
      }
    },
    
    handleFileUpload(event) {
      const file = event.target.files[0]
      
      if (!file) return
      
      // Validação de tamanho
      if (file.size > 5 * 1024 * 1024) {
        this.$sweet.sweetError("O arquivo deve ter no máximo 5MB.")
        event.target.value = ''
        return
      }
      
      // Validação de tipo
      const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png']
      if (!allowedTypes.includes(file.type)) {
        this.$sweet.sweetError("Formato de arquivo não permitido. Use PDF, JPG ou PNG.")
        event.target.value = ''
        return
      }
      
      this.arquivo = file
      console.log('Arquivo selecionado:', file.name, this.formatFileSize(file.size))
    },
    
    removerArquivo() {
      this.arquivo = null
      // Limpar o input file
      const fileInput = document.querySelector('input[type="file"]')
      if (fileInput) fileInput.value = ''
    },
    
    formatFileSize(bytes) {
      if (!bytes) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i]
    },

    formatarData(data) {
      if (!data) return "-";
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year}`;
        }
        
        // Para datas ISO com timezone, remover o 'Z' e tratar como local
        let dateString = data;
        if (typeof data === 'string' && data.includes('T')) {
          dateString = dateString.replace('Z', '');
          if (dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/)) {
            const [datePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-');
            return `${day}/${month}/${year}`;
          }
        }
        
        // Criar data e usar métodos locais
        const dateObj = new Date(dateString);
        if (isNaN(dateObj.getTime())) return "-";
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        return `${dia}/${mes}/${ano}`;
      } catch (error) {
        return "-";
      }
    },

    formatarDataHora(data) {
      if (!data) return "-";
      
      try {
        // Se a data vier apenas com a data (sem hora), usar diretamente
        if (typeof data === 'string' && data.match(/^\d{4}-\d{2}-\d{2}$/)) {
          const [year, month, day] = data.split('-');
          return `${day}/${month}/${year} 00:00`;
        }
        
        // Tratar formato do banco: 2025-11-25 07:53:15.000
        if (typeof data === 'string') {
          // Remover o 'Z' do final se existir (formato ISO)
          let dateString = data.replace('Z', '');
          
          // Se tiver formato com espaço (formato do banco: YYYY-MM-DD HH:mm:ss)
          if (dateString.match(/^\d{4}-\d{2}-\d{2}\s+\d{2}:\d{2}:\d{2}/)) {
            const [datePart, timePart] = dateString.split(' ');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
          
          // Se tiver formato ISO com T (YYYY-MM-DDTHH:mm:ss)
          if (dateString.includes('T')) {
            const [datePart, timePart] = dateString.split('T');
            const [year, month, day] = datePart.split('-');
            const [hours, minutes] = timePart.split(':');
            return `${day}/${month}/${year} ${hours}:${minutes}`;
          }
        }
        
        // Fallback: usar Date object apenas se necessário
        const dateObj = new Date(data);
        if (isNaN(dateObj.getTime())) return "-";
        
        const dia = String(dateObj.getDate()).padStart(2, '0');
        const mes = String(dateObj.getMonth() + 1).padStart(2, '0');
        const ano = dateObj.getFullYear();
        const hora = String(dateObj.getHours()).padStart(2, '0');
        const minuto = String(dateObj.getMinutes()).padStart(2, '0');
        return `${dia}/${mes}/${ano} ${hora}:${minuto}`;
      } catch (error) {
        return "-";
      }
    },
    legendaStatus(status){
      if(status === 'D') return 'Deferido'
      if(status === 'I') return 'Indeferido'
      else return 'Pendente'
    }
  }
}
</script>

<style lang="sass" scoped>
.area-isencao
  background-color: #fff
  padding: 25px 15px
  border-radius: 8px
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)
  
  h5
    font-size: 20px
    font-weight: 600
    text-align: center
    color: #2c3e50
    margin-bottom: 25px
    
    i
      margin-right: 10px
      color: #007bff

// Section headers
.section-header
  font-size: 16px
  font-weight: 600
  color: #2c3e50
  padding: 10px 15px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-left: 4px solid #007bff
  border-radius: 4px
  margin-bottom: 15px
  
  i
    margin-right: 8px
    color: #007bff
  
  span
    vertical-align: middle

// Loading States
.loading-container
  display: flex
  justify-content: center
  align-items: center
  padding: 60px 20px
  min-height: 200px

  .spinner-wrapper
    text-align: center

    .loading-text
      color: #6c757d
      font-size: 14px
      font-weight: 500
      margin-top: 15px

.spinner-border
  width: 3rem
  height: 3rem
  border-width: 0.3em

// Empty state
.empty-state
  text-align: center
  padding: 60px 30px
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 12px
  border: 2px dashed #dee2e6
  margin: 20px 0
  
  .empty-icon
    width: 80px
    height: 80px
    margin: 0 auto 20px
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%)
    border-radius: 50%
    display: flex
    align-items: center
    justify-content: center
    
    i
      font-size: 36px
      color: #fff

  .empty-title
    color: #2c3e50
    font-weight: 600
    font-size: 18px
    margin-bottom: 10px

  .empty-text
    color: #6c757d
    font-size: 15px
    margin-bottom: 8px

  .empty-hint
    color: #adb5bd
    font-size: 13px
    font-style: italic

// Tables
.table-responsive
  margin-top: 15px
  border-radius: 8px
  overflow: hidden
  box-shadow: 0 2px 4px rgba(0,0,0,0.1)

.table
  margin-bottom: 0
  
  thead.table-dark
    background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
    
    th
      color: #fff
      font-weight: 600
      border: none
      padding: 12px
      font-size: 14px
      
      i
        margin-right: 5px
        opacity: 0.8
  
  tbody
    tr
      transition: all 0.3s ease
      
      &:hover
        background-color: #f8f9fa
        transform: scale(1.01)
    
    td
      padding: 12px
      vertical-align: middle
      font-size: 14px
      
      strong
        color: #2c3e50
      
      small
        display: block
        color: #6c757d
        margin-top: 2px

// Link hover effect
.link-hover
  transition: all 0.2s ease
  text-decoration: none
  display: inline-block
  
  &:hover
    text-decoration: underline
    transform: translateX(3px)
    
  i
    margin-right: 5px
    font-size: 12px

// Badge status
.badge-status
  padding: 5px 12px
  border-radius: 12px
  font-size: 12px
  font-weight: 600
  display: inline-block
  
  &.badge-warning
    background-color: #ffc107
    color: #000
  
  &.badge-success
    background-color: #28a745
    color: #fff
  
  &.badge-danger
    background-color: #dc3545
    color: #fff
  
  &.badge-secondary
    background-color: #6c757d
    color: #fff

// Modal Loading Overlay
.modal-loading-overlay
  position: absolute
  top: 0
  left: 0
  right: 0
  bottom: 0
  background: rgba(0, 0, 0, 0.7)
  z-index: 10
  display: flex
  align-items: center
  justify-content: center
  border-radius: 10px
  
  .spinner-wrapper
    text-align: center
    
    .loading-text
      color: #fff
      font-size: 14px
      font-weight: 500

// Info box
.info-box
  background: linear-gradient(135deg, #e3f2fd 0%, #bbdefb 100%)
  padding: 15px
  border-radius: 8px
  border-left: 4px solid #2196f3
  
  h6
    margin: 0 0 8px 0
    font-weight: 600
    
    i
      margin-right: 8px
  
  p
    margin: 0
    line-height: 1.6

// Form elements
.form-group
  margin-bottom: 20px
  
  .form-label
    font-weight: 600
    color: #2c3e50
    margin-bottom: 8px
    display: block
    
    i
      margin-right: 6px
      color: #007bff
    
    .text-danger
      margin-left: 2px

.form-control
  border-radius: 6px
  border: 1px solid #ced4da
  padding: 10px 12px
  transition: all 0.3s ease
  
  &:focus
    border-color: #007bff
    box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25)
  
  &.is-invalid
    border-color: #dc3545
    
    &:focus
      box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25)

.invalid-feedback
  display: block
  margin-top: 5px
  font-size: 13px
  color: #dc3545

.form-text
  font-size: 12px
  margin-top: 5px

// File info
.file-info
  display: flex
  align-items: center
  padding: 10px
  background: #f8f9fa
  border-radius: 6px
  border: 1px solid #dee2e6
  
  i.fa-file
    font-size: 18px
    color: #007bff
    margin-right: 10px
  
  span
    flex: 1
    font-size: 14px
    color: #2c3e50
  
  .file-size
    color: #6c757d
    font-size: 12px
    margin-left: 8px

.btn-remove-file
  background: none
  border: none
  color: #dc3545
  cursor: pointer
  padding: 5px
  border-radius: 50%
  width: 30px
  height: 30px
  display: flex
  align-items: center
  justify-content: center
  transition: all 0.2s ease
  
  &:hover
    background: #dc3545
    color: #fff

// Modal customizado
.modal-overlay
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(0, 0, 0, 0.5)
  z-index: 1050
  display: flex
  align-items: center
  justify-content: center
  padding: 20px
  animation: fadeIn 0.3s ease

.modal-content
  background: white
  border-radius: 10px
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3)
  max-width: 90vw
  max-height: 90vh
  overflow: auto
  position: relative
  animation: slideInUp 0.3s ease
  
  &.large
    width: 700px

.modal-header
  display: flex
  justify-content: space-between
  align-items: center
  padding: 20px 25px
  border-bottom: 1px solid #e9ecef
  background: linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)
  border-radius: 10px 10px 0 0
  
  .modal-title
    margin: 0
    font-size: 18px
    font-weight: 600
    color: #2c3e50
    
    i
      margin-right: 8px
      color: #007bff

.modal-close
  background: none
  border: none
  font-size: 20px
  color: #6c757d
  cursor: pointer
  padding: 5px
  border-radius: 50%
  width: 35px
  height: 35px
  display: flex
  align-items: center
  justify-content: center
  transition: all 0.3s ease
  
  &:hover
    background: #e9ecef
    color: #dc3545

.modal-body
  padding: 25px
  
  .detalhe-texto
    padding: 15px
    background: #f8f9fa
    border-radius: 6px
    border-left: 4px solid #007bff
    line-height: 1.6
    white-space: pre-wrap
    word-wrap: break-word

.modal-footer
  display: flex
  justify-content: flex-end
  padding: 20px 25px
  border-top: 1px solid #e9ecef
  background: #f8f9fa
  border-radius: 0 0 10px 10px
  
  .btn
    margin-left: 10px

// Buttons
.btn
  border-radius: 6px
  padding: 8px 16px
  font-weight: 500
  transition: all 0.3s ease
  border: none
  
  &:hover
    transform: translateY(-2px)
    box-shadow: 0 4px 8px rgba(0,0,0,0.2)
  
  &:disabled
    opacity: 0.6
    cursor: not-allowed
    transform: none
  
  i
    margin-right: 6px
  
  &.btn-sm
    padding: 6px 12px
    font-size: 13px
  
  &.btn-info
    background-color: #17a2b8
    color: #fff
    text-decoration: none
    
    &:hover
      background-color: #138496
      color: #fff
      text-decoration: none

// Animations
@keyframes fadeIn
  from
    opacity: 0
  to
    opacity: 1

@keyframes slideInUp
  from
    opacity: 0
    transform: translateY(20px) scale(0.95)
  to
    opacity: 1
    transform: translateY(0) scale(1)

// Utility classes
.font-13
  font-size: 13px

.pointer
  cursor: pointer

.weight-400
  font-weight: 400

// Mobile Cards
.mobile-cards
  display: flex
  flex-direction: column
  gap: 15px
  margin-top: 15px

.mobile-card
  background: #fff
  border-radius: 8px
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1)
  overflow: hidden
  transition: all 0.3s ease
  
  &:hover
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15)
    transform: translateY(-2px)

.card-header-mobile
  background: linear-gradient(135deg, #2c3e50 0%, #34495e 100%)
  color: #fff
  padding: 15px
  display: flex
  align-items: center
  gap: 10px
  
  i
    font-size: 18px
    opacity: 0.9
  
  strong
    font-size: 14px
    font-weight: 600
    flex: 1
    line-height: 1.4

.card-body-mobile
  padding: 15px
  
  .card-item
    display: flex
    align-items: flex-start
    margin-bottom: 12px
    gap: 8px
    flex-wrap: wrap
    
    &:last-child
      margin-bottom: 0
    
    i
      color: #007bff
      font-size: 16px
      flex-shrink: 0
      min-width: 18px
      margin-top: 2px
    
    .label
      font-weight: 600
      color: #6c757d
      font-size: 13px
      flex-shrink: 0
      min-width: fit-content
    
    .value
      flex: 1
      color: #2c3e50
      font-size: 14px
      line-height: 1.4
      text-align: left
      word-break: break-word
      min-width: 0
      overflow-wrap: break-word

.card-footer-mobile
  padding: 15px
  background: #f8f9fa
  border-top: 1px solid #e9ecef
  
  .btn-block-mobile
    width: 100%
    padding: 12px
    font-size: 14px
    font-weight: 600
    display: flex
    align-items: center
    justify-content: center
    gap: 8px
    
    i
      font-size: 16px

// Responsividade
@media (max-width: 768px)
  .modal-content
    width: 95vw
    max-height: 95vh
    
    &.large
      width: 95vw
  
  .table
    font-size: 12px
    
    th, td
      padding: 8px
  
  .section-header
    font-size: 14px
    padding: 8px 12px

@media (max-width: 576px)
  .modal-content
    width: 98vw
    max-height: 98vh
    
    &.large
      width: 98vw
  
  .area-isencao
    padding: 15px 10px
    
    h5
      font-size: 16px
  
  .empty-state
    padding: 40px 15px
    
    i
      font-size: 36px

  .card-body-mobile
    padding: 12px
    
    .card-item
      gap: 6px
      margin-bottom: 10px
      
      i
        font-size: 14px
        min-width: 16px
      
      .label
        font-size: 12px
      
      .value
        font-size: 13px
        line-height: 1.5

  .card-header-mobile
    padding: 12px
    
    i
      font-size: 16px
    
    strong
      font-size: 13px

  .card-footer-mobile
    padding: 12px
    
    .btn-block-mobile
      padding: 10px
      font-size: 13px
</style>
